import React, {
  ComponentPropsWithoutRef,
  ElementType,
  forwardRef,
  useMemo,
} from 'react';
import clsx from 'clsx';
import MuiSelect, {
  SelectChangeEvent,
} from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import {
  DayPicker as DayPickerLib,
  useDayPicker,
  type DayButtonProps,
  type DayPickerProps,
  type MonthCaptionProps,
} from '@daypicker/react';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import isNil from 'lodash/isNil';
import dayjs from 'utils/dayjs';
import type {
  CalendarProps as PickerCalendarProps,
} from 'core/components/DateTimePicker/types';
import type {
  SelectProps,
} from '@mui/material/Select';
import {
  getYearRange,
  getYearOptions,
  yearMenuProps,
} from 'core/components/DateTimePicker/base/calendarYearSelect';

const focusRing = clsx(
  'outline-none',
  'focus-visible:outline-2',
  'focus-visible:outline-blue-500',
  'focus-visible:outline-offset-2',
);

type NavigationButtonProps = ComponentPropsWithoutRef<'button'> & {
  icon: ElementType;
};

const NavigationButton = forwardRef<
  HTMLButtonElement,
  NavigationButtonProps
>(
  (
    {
      className,
      onClick,
      icon: Icon,
      disabled,
      ...props
    }: NavigationButtonProps,
    forwardedRef,
  ) => (
    <button
      ref={forwardedRef}
      type="button"
      disabled={disabled}
      className={clsx(
        'flex w-8 h-8 select-none items-center justify-center rounded-sm border p-1 outline-hidden transition sm:size-[30px]',
        'text-gray-600 hover:text-gray-800',
        'border-none',
        'hover:bg-gray-50 active:bg-gray-100',
        'disabled:pointer-events-auto',
        'disabled:border-gray-200',
        'disabled:text-gray-400',
        'disabled:cursor-not-allowed disabled:hover:bg-transparent',
        focusRing,
        className,
      )}
      onClick={onClick}
      {...props}
    >
      <Icon className="w-full h-full shrink-0" />
    </button>
  ),
);

NavigationButton.displayName = 'NavigationButton';

const getMonthBoundary = (date: Date | undefined, boundary: 'start' | 'end') => (!isNil(date)
  ? dayjs(date)[boundary === 'start' ? 'startOf' : 'endOf']('month').toDate()
  : undefined);

function getMonthOptions(
  year: number,
  startMonth: Date | undefined,
  endMonth: Date | undefined,
) {
  return Array.from({ length: 12 }, (_, monthIndex) => {
    const optionDate = new Date(year, monthIndex, 1);
    const isBeforeStart = !isNil(startMonth) && optionDate < dayjs(startMonth).startOf('month').toDate();
    const isAfterEnd = !isNil(endMonth) && optionDate > dayjs(endMonth).startOf('month').toDate();

    return {
      value: monthIndex,
      label: dayjs(optionDate).format('MMMM'),
      disabled: isBeforeStart || isAfterEnd,
    };
  });
}
const monthMenuProps: NonNullable<SelectProps<number>['MenuProps']> = {
  slotProps: {
    paper: {
      style: {
        maxHeight: 320,
      },
    },
  },
};

const createMonthCaptionComponent = (
  enableYearNavigation: boolean,
  disableNavigation: boolean | undefined,
) => ({
  calendarMonth,
  displayIndex,
  ...captionProps
}: MonthCaptionProps) => {
  const {
    goToMonth,
    nextMonth,
    previousMonth,
    months,
    dayPickerProps,
  } = useDayPicker();
  const { startMonth, endMonth } = dayPickerProps;

  const isFirst = displayIndex === 0;
  const isLast = displayIndex === months.length - 1;

  const hideNextButton = months.length > 1 && !isLast;
  const hidePreviousButton = months.length > 1 && !isFirst;

  const goToPreviousYear = () => {
    const targetMonth = dayjs(calendarMonth.date).subtract(1, 'year').toDate();
    const minMonth = getMonthBoundary(startMonth, 'start');
    const shouldGoToPreviousYear = !isNil(previousMonth)
      && (isNil(minMonth) || targetMonth.getTime() >= minMonth.getTime());

    return shouldGoToPreviousYear ? goToMonth(targetMonth) : undefined;
  };

  const goToNextYear = () => {
    const targetMonth = dayjs(calendarMonth.date).add(1, 'year').toDate();
    const maxMonth = getMonthBoundary(endMonth, 'end');
    const shouldGoToNextYear = !isNil(nextMonth)
      && (isNil(maxMonth) || targetMonth.getTime() <= maxMonth.getTime());

    return shouldGoToNextYear ? goToMonth(targetMonth) : undefined;
  };

  const captionMonthDate = calendarMonth.date;
  const selectedMonth = dayjs(captionMonthDate).month();
  const selectedYear = dayjs(captionMonthDate).year();
  const minMonth = getMonthBoundary(startMonth, 'start');
  const maxMonth = getMonthBoundary(endMonth, 'end');
  const { startYear, endYear } = getYearRange(minMonth, maxMonth, captionMonthDate);
  const monthOptions = getMonthOptions(selectedYear, minMonth, maxMonth);
  const yearOptions = getYearOptions(startYear, endYear);

  const handleMonthChange = (event: SelectChangeEvent<number>) => {
    goToMonth(new Date(selectedYear, Number(event.target.value), 1));
  };

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    const nextYear = Number(event.target.value);
    const requestedMonth = new Date(nextYear, selectedMonth, 1);
    const nextYearMonthOptions = getMonthOptions(nextYear, minMonth, maxMonth);
    const fallbackMonth = nextYearMonthOptions.find(
      (option) => !option.disabled,
    )?.value ?? selectedMonth;
    const isRequestedMonthDisabled = nextYearMonthOptions
      .find((option) => option.value === selectedMonth)?.disabled ?? false;

    goToMonth(isRequestedMonthDisabled
      ? new Date(nextYear, fallbackMonth, 1)
      : requestedMonth);
  };

  return (
    <div {...captionProps} className={clsx('flex items-center justify-between', captionProps.className)}>
      <div className="flex items-center gap-1">
        {enableYearNavigation && !hidePreviousButton && (
          <NavigationButton
            disabled={disableNavigation || !previousMonth}
            aria-label="Go to previous year"
            onClick={goToPreviousYear}
            icon={KeyboardDoubleArrowLeftIcon}
          />
        )}
        {!hidePreviousButton && (
          <NavigationButton
            disabled={disableNavigation || !previousMonth}
            aria-label="Go to previous month"
            onClick={() => previousMonth && goToMonth(previousMonth)}
            icon={KeyboardArrowLeftIcon}
          />
        )}
      </div>

      <div className="flex items-center gap-4">
        <MuiSelect<number>
          aria-label="Select month"
          className="calendar-caption-select"
          value={selectedMonth}
          onChange={handleMonthChange}
          disabled={disableNavigation}
          size="small"
          variant="standard"
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={monthMenuProps}
        >
          {monthOptions.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={clsx('calendar-caption-option !text-xs !font-medium', {
                'calendar-caption-option-disabled !cursor-not-allowed !pointer-events-auto': option.disabled,
              })}
            >
              {option.label}
            </MenuItem>
          ))}
        </MuiSelect>
        <MuiSelect<number>
          aria-label="Select year"
          className="calendar-caption-select"
          value={selectedYear}
          onChange={handleYearChange}
          disabled={disableNavigation}
          size="small"
          variant="standard"
          IconComponent={KeyboardArrowDownIcon}
          MenuProps={yearMenuProps}
        >
          {yearOptions.map((year) => (
            <MenuItem
              key={year}
              value={year}
              className="calendar-caption-option !text-xs !font-medium"
            >
              {year}
            </MenuItem>
          ))}
        </MuiSelect>
      </div>

      <div className="flex items-center gap-1">
        {!hideNextButton && (
          <NavigationButton
            disabled={disableNavigation || !nextMonth}
            aria-label="Go to next month"
            onClick={() => nextMonth && goToMonth(nextMonth)}
            icon={KeyboardArrowRightIcon}
          />
        )}
        {enableYearNavigation && !hideNextButton && (
          <NavigationButton
            disabled={disableNavigation || !nextMonth}
            aria-label="Go to next year"
            onClick={goToNextYear}
            icon={KeyboardDoubleArrowRightIcon}
          />
        )}
      </div>
    </div>
  );
};

const DayButton = ({
  day: _day,
  modifiers,
  children: buttonChildren,
  className: buttonClassName,
  ...buttonPropsRest
}: DayButtonProps) => {
  const { selected, today, disabled, range_middle: rangeMiddle } = modifiers;

  return (
    <button
      {...buttonPropsRest}
      type="button"
      className={clsx('relative', buttonClassName)}
    >
      {buttonChildren}
      {today && (
        <span
          className={clsx(
            'absolute inset-x-1/2 bottom-1.5 h-0.5 w-4 -translate-x-1/2 rounded-[2px]',
            {
              'bg-blue-500': selected || rangeMiddle,
              'bg-gray-400 text-gray-400': disabled,
              'bg-white': selected && !rangeMiddle,
            },
          )}
        />
      )}
    </button>
  );
};

type CalendarProps = PickerCalendarProps & Partial<DayPickerProps> & {
  enableYearNavigation?: boolean;
  compact?: boolean;
};

export default function Calendar({
  mode = 'single',
  weekStartsOn = 0,
  numberOfMonths = 1,
  enableYearNavigation = false,
  disableNavigation,
  className,
  classNames,
  compact = false,
  ...props
}: CalendarProps) {
  const Caption = useMemo(
    () => createMonthCaptionComponent(enableYearNavigation, disableNavigation),
    [enableYearNavigation, disableNavigation],
  );

  const startMonth = props.startMonth
    ?? props.fromMonth
    ?? (props.fromYear ? new Date(props.fromYear, 0, 1) : undefined)
    ?? getMonthBoundary(props.fromDate, 'start');
  const endMonth = props.endMonth
    ?? props.toMonth
    ?? (props.toYear ? new Date(props.toYear, 11, 1) : undefined)
    ?? getMonthBoundary(props.toDate, 'end');

  const dayPickerProps = {
    ...(props as DayPickerProps),
    mode,
    weekStartsOn,
    numberOfMonths,
    showOutsideDays: numberOfMonths === 1,
    startMonth,
    endMonth,
    hideNavigation: true,
    className: clsx(className),
    classNames: {
      months: compact ? 'flex flex-col space-y-0' : 'flex space-y-0',
      month: compact ? 'space-y-3 p-4' : 'space-y-4 p-3',
      month_caption: compact ? 'flex items-center justify-between px-1' : 'flex items-center justify-between',
      nav: 'gap-1 flex items-center rounded-full w-full h-full justify-between p-4',
      month_grid: compact
        ? 'day_table w-auto table-fixed border-separate border-spacing-x-[4px] border-spacing-y-[6px]'
        : 'day_table w-full table-fixed border-separate border-spacing-y-[4px]',
      weekdays: 'w-full',
      weekday: compact
        ? 'w-9 font-medium text-xs text-center text-gray-400 pb-1'
        : 'w-10 font-medium text-sm sm:text-xs text-center text-gray-400 pb-2',
      weeks: compact ? 'w-auto' : 'w-full',
      week: compact ? 'w-auto' : 'w-full',
      day: clsx(
        'text-gray-900',
        'relative p-0 text-center align-middle',
        {
          'h-9 w-9': compact,
          'h-10 w-10': !compact,
        },
      ),
      day_button: clsx(
        'flex items-center justify-center rounded-full',
        'text-gray-900',
        'hover:bg-gray-200',
        'disabled:pointer-events-none disabled:text-gray-500 disabled:hover:bg-transparent disabled:opacity-100',
        focusRing,
        {
          'h-9 w-9 text-xs': compact,
          'h-10 w-10 text-sm': !compact,
        },
      ),
      today: 'font-semibold',
      selected: 'day_single_selected',
      disabled:
        '!text-gray-500 cursor-not-allowed disabled:hover:bg-transparent',
      outside: 'text-gray-400',
      hidden: 'invisible',
      ...classNames,
    },
    components: {
      MonthCaption: Caption,
      DayButton,
    },
  } as DayPickerProps;

  return (
    <DayPickerLib {...dayPickerProps} />
  );
}
